import { describe } from '../dates';
import { Icon } from './Icon';
import { SLOT_LABEL, type AvailableResource, type MyBooking, type Slot } from '../types';


interface Props {
  date: string;
  resource: AvailableResource | null;
  slot: Slot;
  onSlotChange: (slot: Slot) => void;
  onConfirm: () => void;
  /** Everything this person already holds on this date, both kinds. */
  existing: MyBooking[];
  onCancel: (bookingId: string) => void;
  pending: boolean;
  error: string | null;
  kindLabel: string;
}

function Facts({
  slot,
  onSlotChange,
  resource,
  isParking,
}: {
  slot: Slot;
  onSlotChange: (slot: Slot) => void;
  resource: AvailableResource;
  isParking: boolean;
}) {
  // The date is already the panel heading, so repeating it as a row
  // only adds height on a screen that has none to spare.
  return (
    <div className="facts">
      <div className="fact">
        <Icon name="clock" />
        <span className="pair">
          {resource.freeSlots.length > 1 ? (
            <select
              className="fact-select"
              value={slot}
              onChange={(event) => onSlotChange(event.target.value as Slot)}
              aria-label="Booking time"
            >
              {resource.freeSlots.map((option) => (
                <option key={option} value={option}>
                  {SLOT_LABEL[option]}
                </option>
              ))}
            </select>
          ) : (
            <span className="v">{SLOT_LABEL[slot]}</span>
          )}
          <span className="k">Time</span>
        </span>
      </div>
      <div className="fact">
        <Icon name="desk" />
        <span className="pair">
          <span className="v">
            {resource.label}
            {resource.zone ? ` · ${resource.zone}` : ''}
          </span>
          <span className="k">{isParking ? 'Bay' : 'Desk'}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * What you already hold on the selected day. Shown before anything is
 * picked, because "what have I got today" is the question people open
 * the app to answer, and hunting for a blue dot on a floor plan is a
 * poor way to answer it.
 */
function Held({
  bookings,
  onCancel,
  pending,
}: {
  bookings: MyBooking[];
  onCancel: (id: string) => void;
  pending: boolean;
}) {
  return (
    <div className="held">
      {bookings.map((b) => (
        <div key={b.id} className="held-row">
          <Icon name={b.kind === 'parking' ? 'clock' : 'desk'} />
          <span className="pair">
            <span className="v">
              {b.kind === 'parking' ? 'Bay' : 'Desk'} {b.label}
            </span>
            <span className="k">
              {b.kind === 'parking' ? 'Parking' : b.area_name} · {SLOT_LABEL[b.slot]}
            </span>
          </span>
          <button className="danger" onClick={() => onCancel(b.id)} disabled={pending}>
            {pending ? '…' : 'Cancel'}
          </button>
        </div>
      ))}
    </div>
  );
}

export function BookingPanel({
  date,
  resource,
  slot,
  onSlotChange,
  onConfirm,
  existing,
  onCancel,
  pending,
  error,
  kindLabel,
}: Props) {
  // Selecting something already yours adds nothing: it is listed above
  // with its own cancel button.
  const booking = resource && !resource.mine ? resource : null;

  return (
    <div className="card panel">
      <h3>{describe(date)}</h3>

      {existing.length > 0 && (
        <Held bookings={existing} onCancel={onCancel} pending={pending} />
      )}

      {booking && (
        <>
          <Facts
            slot={slot}
            onSlotChange={onSlotChange}
            resource={booking}
            isParking={kindLabel === 'Parking'}
          />

          <button className="primary" onClick={onConfirm} disabled={pending}>
            {pending ? 'Booking…' : 'Confirm booking'}
          </button>
        </>
      )}

      {error && <p className="notice">{error}</p>}

      {!booking && existing.length === 0 && (
        <p className="muted">
          Pick {kindLabel === 'Parking' ? 'a bay' : 'a desk'} to book it.
        </p>
      )}

      {!booking && existing.length > 0 && (
        <p className="muted">
          Cancelling frees it for someone else straight away.
        </p>
      )}
    </div>
  );
}
